import { supabase } from './supabase'
import {
  CreateOrderParams,
  CreateOrderItemParams,
  CreateOrderItemSelectionParams,
  InsertedOrder,
  InsertedOrderItem,
} from '../types/restaurant'

// ─── Client Fingerprint ──────────────────────────────────────

const FP_STORAGE_KEY = 'client_fp'

/**
 * Generate or retrieve a stable client fingerprint for order deduplication.
 * Persisted in localStorage; falls back to a fresh UUID if storage fails.
 */
export function getClientFingerprint(): string {
  try {
    const existing = localStorage.getItem(FP_STORAGE_KEY)
    if (existing) return existing

    const fp = crypto.randomUUID()
    localStorage.setItem(FP_STORAGE_KEY, fp)
    return fp
  } catch {
    return crypto.randomUUID()
  }
}

// ─── Order Creation ──────────────────────────────────────────

/**
 * Insert a single order row into the `orders` table.
 * Returns the inserted row or throws on failure.
 */
export async function createOrder(
  params: CreateOrderParams,
): Promise<InsertedOrder> {
  const payload = {
    restaurant_id: params.restaurant_id,
    order_number: params.order_number,
    tracking_token: params.tracking_token,
    client_fingerprint: params.client_fingerprint,
    customer_name: params.customer_name,
    customer_phone: params.customer_phone,
    delivery_type: params.delivery_type,
    address: params.address || null,
    location_url: params.location_url || null,
    payment_method: params.payment_method,
    subtotal: params.subtotal,
    express_fee: params.express_fee,
    total_final: params.total_final,
    image_url: params.image_url,
    order_text: params.order_text,
    status: 'new' as const,
    table_number: params.table_number || null,
    service_mode: params.service_mode || null,
  }

  const { data, error } = await supabase.from('orders').insert(payload).select()

  if (error) {
    console.error('[order-service] createOrder error:', error)
    throw error
  }

  if (!data || data.length === 0) {
    throw new Error('Order insert returned no data')
  }

  return data[0] as InsertedOrder
}

// ─── Order Items ─────────────────────────────────────────────

/**
 * Insert one or more order item rows into the `order_items` table.
 * Returns the inserted rows (with their generated IDs) or throws on failure.
 */
export async function createOrderItems(
  items: CreateOrderItemParams[],
): Promise<InsertedOrderItem[]> {
  if (items.length === 0) return []

  const { data, error } = await supabase
    .from('order_items')
    .insert(items)
    .select()

  if (error) {
    console.error('[order-service] createOrderItems error:', error)
    throw error
  }

  return (data || []) as InsertedOrderItem[]
}

// ─── Order Item Selections ───────────────────────────────────

/**
 * Insert one or more order item selection rows into `order_item_selections`.
 * These record which options the customer chose for each order item.
 */
export async function createOrderItemSelections(
  selections: CreateOrderItemSelectionParams[],
): Promise<void> {
  if (selections.length === 0) return

  const { error } = await supabase
    .from('order_item_selections')
    .insert(selections)

  if (error) {
    console.error('[order-service] createOrderItemSelections error:', error)
    throw error
  }
}

// ─── Full Order Orchestrator ─────────────────────────────────

export interface FullOrderItem {
  product_name: string
  quantity: number
  price: number
  station: string | null
  notes: string | null
  /** Selections the customer made for this item */
  selections?: {
    option_group_name: string
    option_name: string
    price: number
  }[]
}

export interface CreateFullOrderParams {
  order: CreateOrderParams
  items: FullOrderItem[]
}

export interface CreateFullOrderResult {
  order: InsertedOrder
  orderItems: InsertedOrderItem[]
}

/**
 * Create a complete order in one call: order → order_items → order_item_selections.
 *
 * This orchestrator ensures all three inserts happen sequentially and that
 * each child row receives the correct parent ID. If any step fails, the
 * error is thrown so the caller can handle rollback / retry.
 */
export async function createFullOrder(
  params: CreateFullOrderParams,
): Promise<CreateFullOrderResult> {
  const { order: orderParams, items: itemDefs } = params
  const restaurantId = orderParams.restaurant_id

  // 1) Insert order
  const order = await createOrder(orderParams)

  // 2) Insert order items
  const orderItemPayloads: CreateOrderItemParams[] = itemDefs.map((item) => ({
    restaurant_id: restaurantId,
    order_id: order.id,
    product_name: item.product_name,
    quantity: item.quantity,
    price: item.price,
    station: item.station,
    notes: item.notes,
  }))

  const orderItems = await createOrderItems(orderItemPayloads)

  // 3) Insert order item selections (flatten across all items)
  const selectionPayloads: CreateOrderItemSelectionParams[] = []

  itemDefs.forEach((itemDef, index) => {
    const insertedItem = orderItems[index]
    if (!insertedItem || !itemDef.selections?.length) return

    itemDef.selections.forEach((sel) => {
      selectionPayloads.push({
        restaurant_id: restaurantId,
        order_item_id: insertedItem.id,
        option_group_name: sel.option_group_name,
        option_name: sel.option_name,
        price: sel.price,
      })
    })
  })

  if (selectionPayloads.length > 0) {
    await createOrderItemSelections(selectionPayloads)
  }

  return { order, orderItems }
}
